import React, {forwardRef, memo} from 'react';
import styled, {css} from 'styled-components';

import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {composerDetailsTokenBoxStyle} from '../../../../../libs/shared/ui/src/styles/composerStyles';
import {buildContactDisplayHandle, buildContactDisplayName} from '../../../../helpers/models/contactHelpers';
import {VisualRecipientModel} from '../../../../models/api/visualRecipientModel';

/*
 * Props.
 */

export interface RecipientsEditorItemProps {
  recipient: VisualRecipientModel;
  isSelected?: boolean;
}

/*
 * Style.
 */

interface TokenStyleProps {
  $isSelected?: boolean;
}
const StyledTokenDiv = styled.div<TokenStyleProps>`
  user-select: none;

  ${composerDetailsTokenBoxStyle};
  padding-left: 6px;
  padding-right: 6px;
  margin-right: 4px;

  border-radius: 4px;
  color: ${(p) => p.theme.greys.shade90};
  background-color: ${(p) => p.theme.alphas.black30};

  ${(p) => addColorStyles(p)};
`;

function addColorStyles(props: TokenStyleProps) {
  if (props.$isSelected) {
    return css`
      color: ${(p) => p.theme.greys.white};
      background-color: ${(p) => p.theme.greys.shade70};
    `;
  }

  return css`
    &:hover {
      color: ${(p) => p.theme.greys.shade90};
      background-color: ${(p) => p.theme.alphas.black40};
    }
  `;
}

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  text-overflow: ellipsis;
  overflow: hidden;
`;

/*
 * Component.
 */

export const RecipientsEditorItem = memo(
  forwardRef<HTMLDivElement, RecipientsEditorItemProps>((props, ref) => (
    <StyledTokenDiv ref={ref} $isSelected={props.isSelected} draggable>
      <StyledTooltipCoordinator render={() => renderTooltip(props.recipient)} hideOnMouseDown>
        {buildContactDisplayName(props.recipient)}
      </StyledTooltipCoordinator>
    </StyledTokenDiv>
  )),
);

function renderTooltip(recipient: VisualRecipientModel) {
  const recipientName = buildContactDisplayName(recipient);
  const recipientHandle = buildContactDisplayHandle(recipient);

  // Nothing more to display than what is already visible, ignore.
  if (!recipientHandle || recipientName === recipientHandle) {
    return null;
  }

  return recipientHandle;
}
